import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { authService } from '@/services/auth.service'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Card } from '@/components/ui/Card'
import { validateEmail } from '@/utils/validation'
import { AppLogo } from '@/components/ui/AppLogo'
import { CheckCircle, Envelope } from 'phosphor-react'

export const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!email.trim()) {
      setError("L'email est requis")
      return
    }

    if (!validateEmail(email)) {
      setError('Veuillez entrer une adresse email valide')
      return
    }

    setIsLoading(true)
    try {
      await authService.forgetPassword({ email: email.trim() })
      setIsSuccess(true)
    } catch (error) {
      setError(
        error instanceof Error ? error.message : "Une erreur s'est produite. Veuillez réessayer."
      )
    } finally {
      setIsLoading(false)
    }
  }

  if (isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[var(--color-gray-50)] via-white to-[var(--color-gray-100)] px-4 py-8 sm:py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-10 sm:mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 sm:w-24 sm:h-24 bg-[var(--color-success)] rounded-3xl mb-6 shadow-lg">
              <CheckCircle className="w-10 h-10 sm:w-12 sm:h-12 text-white" weight="regular" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-[var(--color-text-primary)] mb-3">
              Vérifiez votre email
            </h1>
            <p className="text-base sm:text-lg text-[var(--color-text-secondary)]">
              Nous avons envoyé les instructions de réinitialisation à
              <br />
              <span className="font-medium text-[var(--color-text-primary)]">{email}</span>
            </p>
          </div>

          <Card padding="lg">
            <div className="text-center space-y-6">
              <p className="text-base sm:text-lg text-[var(--color-text-secondary)]">
                Veuillez vérifier votre email et suivre les instructions pour réinitialiser votre
                mot de passe.
              </p>
              <div className="pt-2">
                <Button variant="outline" onClick={() => navigate('/login')} fullWidth size="lg">
                  Retour à la connexion
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[var(--color-gray-50)] via-white to-[var(--color-gray-100)] px-4 py-8 sm:py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-10 sm:mb-12">
          <AppLogo size="lg" className="mx-auto mb-6 shadow-lg" />
          <h1 className="text-3xl sm:text-4xl font-bold text-[var(--color-text-primary)] mb-3">
            Réinitialiser le mot de passe
          </h1>
          <p className="text-base sm:text-lg text-[var(--color-text-secondary)]">
            Entrez votre adresse email et nous vous enverrons les instructions pour réinitialiser
            votre mot de passe.
          </p>
        </div>

        <Card padding="lg">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div
                className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm"
                role="alert"
              >
                {error}
              </div>
            )}

            <Input
              label="Adresse email"
              type="email"
              placeholder="vous@exemple.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              autoComplete="email"
              autoFocus
              leftIcon={<Envelope className="w-5 h-5" weight="regular" />}
            />

            <div className="pt-2">
              <Button type="submit" fullWidth isLoading={isLoading} size="lg">
                Envoyer les instructions
              </Button>
            </div>
          </form>
        </Card>

        <div className="mt-8 text-center">
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="text-sm sm:text-base text-[var(--color-primary)] hover:text-[var(--color-primary-dark)] font-medium transition-colors"
          >
            ← Retour à la connexion
          </button>
        </div>
      </div>
    </div>
  )
}
